/**
 * LiveExecutionStatus — polls a fabric-backup-{env} container execution (backup OR
 * restore) and shows a running indicator + elapsed clock + final outcome, so the
 * operator always knows the job is alive and how it ended (FR-023a).
 *
 * The Spec-200/181 engine reports execution STATUS only (not per-table % or live
 * size), so "progress" is a running indicator + elapsed time, resolving to the
 * Succeeded/Failed outcome with total duration. Polling stops once terminal.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardBody, Badge, AppTooltip } from '@/components/ui';
import { formatEasternTime } from '@/utils/dateFormatter';
import { restoreApi } from '../../api/restore';
import { useBackupActivity } from '../../store/backupActivityStore';

// Azure execution states PLUS the backup DB statuses (PascalCase → lowercased). A finished
// backup reports 'Completed'/'Partial'/'Refused'; the old Azure-only list missed those, so a
// finished job read as still running. Include them all.
const TERMINAL = ['succeeded', 'completed', 'failed', 'partial', 'refused', 'cancelled', 'degraded', 'stopped'];
// No real backup/restore runs this long (the backend reaper settles stuck runs at 180 min).
// Past this, a still-"Running" entry is a job that finished or crashed without its terminal
// status ever being observed — e.g. an OOM-killed container whose run row was left Running,
// or an execution Azure has since purged. Treat it as ended rather than spinning forever.
const STALE_MS = 3.5 * 60 * 60 * 1000;
const fmtElapsed = (s: number) =>
  s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;

export const LiveExecutionStatus: React.FC<{ executionName: string; verb: string; subject?: string; startedAt?: number }> = ({ executionName, verb, subject, startedAt }) => {
  const q = useQuery({
    queryKey: ['backup', 'exec-status', executionName],
    queryFn: () => restoreApi.progress(executionName),
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false; // execution gone/purged — stop polling
      const st = String((query.state.data as { status?: string } | undefined)?.status ?? '').toLowerCase();
      return TERMINAL.includes(st) ? false : 4000;
    },
  });
  const raw = q.data?.status as string | undefined;
  const s = String(raw ?? '').toLowerCase();
  const started = q.data?.startTime
    ? new Date(q.data.startTime as string)
    : startedAt != null
      ? new Date(startedAt)
      : null;
  // A finished/crashed job whose execution was purged (or an errored/empty fetch) returns no
  // status — do NOT default it to "Running". Cap by elapsed time as a backstop for a run row
  // left stuck at Running (the exact "shows jobs running that have finished/crashed" bug).
  const dataMissing = !q.isLoading && (q.isError || raw == null);
  const stale = started != null && Date.now() - started.getTime() > STALE_MS;
  const running = !dataMissing && !stale && !TERMINAL.includes(s);
  const displayStatus = raw ?? (stale ? 'Stale' : 'Ended');
  // Spec 214 FR-026 (Q5): terminal — including stale/missing — drops the entry from the
  // header badge count (the drawer keeps it as history).
  const markTerminal = useBackupActivity((st) => st.markTerminal);
  useEffect(() => {
    if (!running) markTerminal(executionName);
  }, [running, executionName, markTerminal]);
  const ok = s === 'succeeded' || s === 'completed';
  const failed = s === 'failed' || s === 'degraded' || s === 'partial' || s === 'refused';
  const ended = q.data?.endTime ? new Date(q.data.endTime as string).getTime() : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [running]);
  const elapsed = started ? Math.max(0, Math.floor(((running ? now : ended ?? now) - started.getTime()) / 1000)) : null;

  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-3">
          {running ? (
            <RefreshCw className="h-5 w-5 animate-spin text-[var(--color-accent)]" />
          ) : ok ? (
            <CheckCircle2 className="h-5 w-5 text-[var(--color-status-success)]" />
          ) : (
            <AlertCircle className={`h-5 w-5 ${failed ? 'text-[var(--color-status-danger)]' : 'text-[var(--color-text-secondary)]'}`} />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-[var(--color-text-primary)]">
                {verb} {ok ? 'completed' : failed ? 'failed' : running ? 'running…' : 'ended'}
                {subject ? ` — ${subject}` : ''}
              </span>
              <Badge variant={ok ? 'success' : failed ? 'danger' : running ? 'info' : 'secondary'}>{displayStatus}</Badge>
            </div>
            <div className="truncate text-xs text-[var(--color-text-secondary)]">
              {executionName}
              {started ? ` · started ${formatEasternTime(started)}` : ''}
              {elapsed != null ? ` · ${fmtElapsed(elapsed)} ${running ? 'elapsed' : 'total'}` : ''}
            </div>
          </div>
          <AppTooltip title="Refresh now">
            <button type="button" onClick={() => q.refetch()} className="rounded border border-[var(--color-border)] p-1 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-accent)]">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </AppTooltip>
        </div>
        {running && (
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
            <div className="h-full w-1/3 rounded-full" style={{ backgroundColor: 'var(--color-accent)', animation: 'execslide 1.4s ease-in-out infinite' }} />
          </div>
        )}
      </CardBody>
      <style>{`@keyframes execslide { 0% { transform: translateX(-120%); } 100% { transform: translateX(420%); } }`}</style>
    </Card>
  );
};

export default LiveExecutionStatus;
